// Small presentational primitives for the incident form.
// Deliberately dumb: no data fetching, no form state. They exist so the
// portal form and the in-app (webview) form render identically.
import { ReactNode } from "react";

type SectionProps = {
  title: string;
  hint?: string;
  children: ReactNode;
  /** Always-open section with no header chrome. */
  static?: boolean;
  /** Marks the whole section as required, same star as FwField. */
  required?: boolean;
  /** Same advisory "confirm this" chip FwField carries, for a section whose
   *  value isn't a single field — the map pin, for instance. */
  needsCheck?: boolean;
  open?: boolean;
  onToggle?: () => void;
};

export function FwSection({
  title,
  hint,
  children,
  static: isStatic,
  required,
  needsCheck,
  open,
  onToggle,
}: SectionProps) {
  // A static section keeps its title — orientation matters more than chrome —
  // but drops the disclosure control, because it can never be closed.
  if (isStatic) {
    return (
      <div className="fw-section fw-section--static">
        <div className="fw-section__header fw-section__header--static">
          <span>
            {title}
            {required && (
              <span className="fw-field__req" aria-hidden>
                *
              </span>
            )}
            {needsCheck && (
              <span className="fw-field__check" title="Read from the dispatch — please confirm">
                check this
              </span>
            )}
            {hint ? <span className="fw-section__hint"> · {hint}</span> : null}
          </span>
        </div>
        <div className="fw-section__panel">
          <div>
            <div className="fw-section__body">{children}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`fw-section${open ? " fw-section--open" : ""}`}>
      <button
        type="button"
        className="fw-section__header"
        aria-expanded={open}
        onClick={onToggle}
      >
        <span>
          {title}
          {required && (
            <span className="fw-field__req" aria-hidden>
              *
            </span>
          )}
          {needsCheck && (
            <span className="fw-field__check" title="Read from the dispatch — please confirm">
              check this
            </span>
          )}
          {hint ? <span className="fw-section__hint"> · {hint}</span> : null}
        </span>
        <i className="ri-arrow-down-s-line fw-section__chev" aria-hidden />
      </button>
      <div className="fw-section__panel">
        <div>
          <div className="fw-section__body">{children}</div>
        </div>
      </div>
    </div>
  );
}

type FieldProps = {
  label: string;
  htmlFor?: string;
  required?: boolean;
  error?: string;
  /** Renders a text button on the right of the label (e.g. "Clear"). */
  action?: { label: string; onClick: () => void };
  /** Marks a value the dispatch parser was unsure of, so the admin's eye lands
   *  on it. Purely advisory — it never blocks anything. */
  needsCheck?: boolean;
  children: ReactNode;
};

export function FwField({
  label,
  htmlFor,
  required,
  error,
  action,
  needsCheck,
  children,
}: FieldProps) {
  return (
    <div className={`fw-field${needsCheck ? " fw-field--check" : ""}`}>
      <label className="fw-field__label" htmlFor={htmlFor}>
        <span>
          {label}
          {required && (
            <span className="fw-field__req" aria-hidden>
              *
            </span>
          )}
          {needsCheck && (
            <span className="fw-field__check" title="Read from the dispatch — please confirm">
              check this
            </span>
          )}
        </span>
        {action && (
          <button
            type="button"
            className="fw-field__clear"
            onClick={action.onClick}
          >
            {action.label}
          </button>
        )}
      </label>
      {children}
      {error ? <p className="fw-field__error">{error}</p> : null}
    </div>
  );
}

type ToggleProps = {
  title: string;
  sub?: string;
  checked: boolean;
  onChange: (next: boolean) => void;
};

export function FwToggle({ title, sub, checked, onChange }: ToggleProps) {
  return (
    <button
      type="button"
      className="fw-toggle"
      aria-pressed={checked}
      onClick={() => onChange(!checked)}
    >
      <span className="fw-toggle__text">
        <span className="fw-toggle__title">{title}</span>
        {sub ? <span className="fw-toggle__sub">{sub}</span> : null}
      </span>
      <span className="fw-toggle__track" aria-hidden>
        <span className="fw-toggle__knob" />
      </span>
    </button>
  );
}

type CheckProps = {
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
};

export function FwCheck({ label, checked, onChange }: CheckProps) {
  return (
    <button
      type="button"
      className="fw-check"
      aria-pressed={checked}
      onClick={() => onChange(!checked)}
    >
      <span className="fw-check__box" aria-hidden>
        <i className="ri-check-line" />
      </span>
      <span>{label}</span>
    </button>
  );
}

type ActionBarProps = {
  submitting: boolean;
  submitLabel: string;
  onSubmit: (e: any) => void;
  onCancel: () => void;
};

export function FwActionBar({
  submitting,
  submitLabel,
  onSubmit,
  onCancel,
}: ActionBarProps) {
  return (
    <div className="fw-actionbar">
      <button
        type="button"
        className="fw-btn fw-btn--ghost"
        onClick={onCancel}
        disabled={submitting}
      >
        Cancel
      </button>
      <button
        type="button"
        className="fw-btn fw-btn--primary"
        onClick={onSubmit}
        disabled={submitting}
      >
        {submitting ? "Saving…" : submitLabel}
      </button>
    </div>
  );
}
